import { createStore, type StoreApi } from "zustand/vanilla";

import {
  getCaptionCueAt,
  toFallbackCaption,
  toTimedCaption,
  type CaptionDisplay
} from "./captions";
import type {
  AudioCatalog,
  AudioChannel,
  AudioSource,
  MusicTrackDefinition,
  SfxTrackDefinition,
  VoiceTrackDefinition
} from "./catalog";
import {
  BrowserAudioPlatform,
  type AudioMediaElement,
  type AudioMediaEvent,
  type AudioPlatform
} from "./platform";
import {
  clampVolume,
  createAudioPreferencesStore,
  updateAudioPreferences,
  type AudioPreferences,
  type AudioPreferencesStore
} from "./preferences";

export type AudioUnlockState = "locked" | "unlocking" | "unlocked";

export type AudioIssueReason =
  | "autoplay-blocked"
  | "destroyed"
  | "locked"
  | "media-error"
  | "missing-track"
  | "nothing-to-replay"
  | "unlock-failed"
  | "unsupported-browser"
  | "unsupported-format";

export interface AudioIssue {
  readonly channel: AudioChannel | null;
  readonly trackId: string | null;
  readonly reason: AudioIssueReason;
}

export interface AudioRuntimeSnapshot {
  readonly unlockState: AudioUnlockState;
  readonly activeMusicId: string | null;
  readonly activeVoiceId: string | null;
  readonly activeSfxCount: number;
  readonly lastVoiceId: string | null;
  readonly caption: CaptionDisplay | null;
  readonly lastIssue: AudioIssue | null;
}

export type AudioPlaybackResult =
  | {
      readonly status: "playing";
      readonly channel: AudioChannel;
      readonly trackId: string;
      readonly playbackId?: number;
    }
  | {
      readonly status: "blocked";
      readonly channel: AudioChannel;
      readonly trackId: string;
      readonly reason: "autoplay-blocked" | "locked";
    }
  | {
      readonly status: "unavailable";
      readonly channel: AudioChannel;
      readonly trackId: string | null;
      readonly reason:
        | "destroyed"
        | "media-error"
        | "missing-track"
        | "nothing-to-replay"
        | "unsupported-browser"
        | "unsupported-format";
    }
  | {
      readonly status: "cancelled";
      readonly channel: AudioChannel;
      readonly trackId: string;
      readonly reason: "superseded";
    };

export type AudioUnlockResult =
  | { readonly status: "unlocked" }
  | {
      readonly status: "failed";
      readonly reason: "destroyed" | "unlock-failed";
    };

export interface AudioRuntimeOptions {
  readonly platform?: AudioPlatform;
  readonly preferencesStore?: AudioPreferencesStore;
  readonly duckingFactor?: number;
}

type RuntimeTrackDefinition =
  | MusicTrackDefinition
  | VoiceTrackDefinition
  | SfxTrackDefinition;

interface ActivePlayback {
  readonly channel: AudioChannel;
  readonly trackId: string;
  readonly track: RuntimeTrackDefinition;
  readonly element: AudioMediaElement;
  readonly sources: readonly AudioSource[];
  readonly sourceIndex: number;
  readonly lateFallbackAttempted: boolean;
  readonly listeners: Array<{
    readonly event: AudioMediaEvent;
    readonly listener: EventListener;
  }>;
}

interface PendingPlayback {
  readonly channel: AudioChannel;
  readonly element: AudioMediaElement;
  cancelled: boolean;
}

type StartElementResult =
  | {
      readonly status: "started";
      readonly element: AudioMediaElement;
      readonly sources: readonly AudioSource[];
      readonly sourceIndex: number;
    }
  | { readonly status: "blocked" }
  | { readonly status: "cancelled" }
  | {
      readonly status: "unavailable";
      readonly reason:
        | "media-error"
        | "unsupported-browser"
        | "unsupported-format";
    };

const INITIAL_RUNTIME_STATE: AudioRuntimeSnapshot = {
  unlockState: "locked",
  activeMusicId: null,
  activeVoiceId: null,
  activeSfxCount: 0,
  lastVoiceId: null,
  caption: null,
  lastIssue: null
};

export class AudioRuntime {
  readonly stateStore: StoreApi<AudioRuntimeSnapshot>;
  readonly preferencesStore: AudioPreferencesStore;

  private readonly platform: AudioPlatform;
  private readonly duckingFactor: number;
  private readonly unsubscribePreferences: () => void;
  private music: ActivePlayback | undefined;
  private voice: ActivePlayback | undefined;
  private readonly sfx = new Set<ActivePlayback>();
  private readonly pendingPlaybacks = new Set<PendingPlayback>();
  private musicRequest = 0;
  private voiceRequest = 0;
  private lifecycleVersion = 0;
  private unlockPromise: Promise<AudioUnlockResult> | undefined;
  private destroyed = false;

  constructor(
    private readonly catalog: AudioCatalog,
    options: AudioRuntimeOptions = {}
  ) {
    this.platform = options.platform ?? new BrowserAudioPlatform();
    this.preferencesStore =
      options.preferencesStore ?? createAudioPreferencesStore();
    this.duckingFactor = clampVolume(options.duckingFactor ?? 0.3);
    this.stateStore = createStore<AudioRuntimeSnapshot>(() => ({
      ...INITIAL_RUNTIME_STATE
    }));
    this.unsubscribePreferences = this.preferencesStore.subscribe(() => {
      this.applyAllVolumes();
    });
  }

  getSnapshot(): AudioRuntimeSnapshot {
    return this.stateStore.getState();
  }

  getPreferences(): AudioPreferences {
    return this.preferencesStore.getState();
  }

  subscribe(
    listener: (
      state: AudioRuntimeSnapshot,
      previousState: AudioRuntimeSnapshot
    ) => void
  ): () => void {
    return this.stateStore.subscribe(listener);
  }

  async unlock(): Promise<AudioUnlockResult> {
    if (this.destroyed) {
      return { status: "failed", reason: "destroyed" };
    }

    if (this.stateStore.getState().unlockState === "unlocked") {
      return { status: "unlocked" };
    }

    if (this.unlockPromise) {
      return this.unlockPromise;
    }

    this.stateStore.setState({ unlockState: "unlocking" });
    const unlockPromise = this.performUnlock();
    this.unlockPromise = unlockPromise;

    try {
      return await unlockPromise;
    } finally {
      if (this.unlockPromise === unlockPromise) {
        this.unlockPromise = undefined;
      }
    }
  }

  async playMusic(trackId: string): Promise<AudioPlaybackResult> {
    const track = this.catalog.music[trackId];
    if (!track) {
      return this.unavailable("music", trackId, "missing-track");
    }

    const guardResult = this.guardPlayback("music", trackId);
    if (guardResult) {
      return guardResult;
    }

    const request = ++this.musicRequest;
    this.cancelPendingPlaybacks("music");
    this.releaseMusic();
    const started = await this.startElement(
      "music",
      track,
      track.loop ?? true
    );

    if (started.status === "cancelled") {
      return this.cancelled("music", trackId);
    }

    if (started.status !== "started") {
      return this.handleStartFailure("music", trackId, started);
    }

    if (request !== this.musicRequest || this.destroyed) {
      this.releaseElement(started.element);
      return this.cancelled("music", trackId);
    }

    const active = this.createActivePlayback(
      "music",
      trackId,
      track,
      started
    );
    this.music = active;
    this.bindPlaybackEvents(active);
    this.stateStore.setState({
      activeMusicId: trackId,
      lastIssue: null
    });
    this.applyAllVolumes();

    return { status: "playing", channel: "music", trackId };
  }

  async playVoice(trackId: string): Promise<AudioPlaybackResult> {
    const track = this.catalog.voice[trackId];
    if (!track) {
      return this.unavailable("voice", trackId, "missing-track");
    }

    this.stateStore.setState({ lastVoiceId: trackId });
    const guardResult = this.guardPlayback("voice", trackId);
    if (guardResult) {
      this.showFallbackCaption(trackId, track);
      return guardResult;
    }

    const request = ++this.voiceRequest;
    this.cancelPendingPlaybacks("voice");
    this.releaseVoice(null);
    const started = await this.startElement("voice", track, false);

    if (started.status === "cancelled") {
      return this.cancelled("voice", trackId);
    }

    if (started.status !== "started") {
      const result = this.handleStartFailure("voice", trackId, started);
      this.showFallbackCaption(trackId, track);
      return result;
    }

    if (request !== this.voiceRequest || this.destroyed) {
      this.releaseElement(started.element);
      return this.cancelled("voice", trackId);
    }

    const active = this.createActivePlayback(
      "voice",
      trackId,
      track,
      started
    );
    this.voice = active;
    this.bindPlaybackEvents(active);
    this.stateStore.setState({
      activeVoiceId: trackId,
      lastVoiceId: trackId,
      lastIssue: null
    });
    this.syncVoiceCaption(active, track);
    this.applyAllVolumes();

    return {
      status: "playing",
      channel: "voice",
      trackId,
      playbackId: request
    };
  }

  async replayVoice(): Promise<AudioPlaybackResult> {
    const trackId = this.stateStore.getState().lastVoiceId;
    if (!trackId) {
      return this.unavailable("voice", null, "nothing-to-replay");
    }

    return this.playVoice(trackId);
  }

  async playSfx(trackId: string): Promise<AudioPlaybackResult> {
    const track = this.catalog.sfx[trackId];
    if (!track) {
      return this.unavailable("sfx", trackId, "missing-track");
    }

    const guardResult = this.guardPlayback("sfx", trackId);
    if (guardResult) {
      return guardResult;
    }

    const lifecycleVersion = this.lifecycleVersion;
    const started = await this.startElement("sfx", track, false);
    if (started.status === "cancelled") {
      return this.cancelled("sfx", trackId);
    }

    if (started.status !== "started") {
      return this.handleStartFailure("sfx", trackId, started);
    }

    if (lifecycleVersion !== this.lifecycleVersion || this.destroyed) {
      this.releaseElement(started.element);
      return this.cancelled("sfx", trackId);
    }

    const active = this.createActivePlayback(
      "sfx",
      trackId,
      track,
      started
    );
    this.sfx.add(active);
    this.bindPlaybackEvents(active);
    this.stateStore.setState({
      activeSfxCount: this.sfx.size,
      lastIssue: null
    });
    this.applyElementVolume(active);

    return { status: "playing", channel: "sfx", trackId };
  }

  stopMusic(): void {
    this.musicRequest += 1;
    this.cancelPendingPlaybacks("music");
    this.releaseMusic();
  }

  stopVoice(playbackId?: number): void {
    if (playbackId !== undefined && playbackId !== this.voiceRequest) {
      return;
    }

    this.voiceRequest += 1;
    this.cancelPendingPlaybacks("voice");
    this.releaseVoice(null);
  }

  stopAll(): void {
    this.musicRequest += 1;
    this.voiceRequest += 1;
    this.lifecycleVersion += 1;
    this.cancelPendingPlaybacks();
    this.releaseMusic();
    this.releaseVoice(null);

    for (const active of this.sfx) {
      this.cleanupPlayback(active);
    }
    this.sfx.clear();
    this.stateStore.setState({ activeSfxCount: 0 });
  }

  setMuted(muted: boolean): AudioPreferences {
    return updateAudioPreferences(this.preferencesStore, { muted });
  }

  toggleMuted(): AudioPreferences {
    return this.setMuted(!this.preferencesStore.getState().muted);
  }

  setMasterVolume(masterVolume: number): AudioPreferences {
    return updateAudioPreferences(this.preferencesStore, { masterVolume });
  }

  setChannelVolume(
    channel: AudioChannel,
    volume: number
  ): AudioPreferences {
    return updateAudioPreferences(this.preferencesStore, {
      channelVolumes: { [channel]: volume }
    });
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.stopAll();
    this.unsubscribePreferences();
    this.stateStore.setState({
      unlockState: "locked",
      caption: null
    });
  }

  private async performUnlock(): Promise<AudioUnlockResult> {
    try {
      await this.platform.unlock();
      if (this.destroyed) {
        return { status: "failed", reason: "destroyed" };
      }

      this.stateStore.setState({
        unlockState: "unlocked",
        lastIssue: null
      });
      return { status: "unlocked" };
    } catch {
      const issue: AudioIssue = {
        channel: null,
        trackId: null,
        reason: "unlock-failed"
      };
      this.stateStore.setState({ unlockState: "locked", lastIssue: issue });
      return { status: "failed", reason: "unlock-failed" };
    }
  }

  private guardPlayback(
    channel: AudioChannel,
    trackId: string
  ): AudioPlaybackResult | undefined {
    if (this.destroyed) {
      return this.unavailable(channel, trackId, "destroyed");
    }

    if (this.stateStore.getState().unlockState !== "unlocked") {
      return this.blocked(channel, trackId, "locked");
    }

    return undefined;
  }

  private async startElement(
    channel: AudioChannel,
    track: RuntimeTrackDefinition,
    loop: boolean,
    requestedSources: readonly AudioSource[] = track.sources
  ): Promise<StartElementResult> {
    let element: AudioMediaElement | undefined;
    try {
      element = this.platform.createAudioElement();
    } catch {
      return { status: "unavailable", reason: "unsupported-browser" };
    }

    if (!element) {
      return { status: "unavailable", reason: "unsupported-browser" };
    }

    const sources = this.getSupportedSources(element, requestedSources);
    if (sources.length === 0) {
      this.releaseElement(element);
      return { status: "unavailable", reason: "unsupported-format" };
    }

    element.loop = loop;
    element.preload = "auto";
    this.applyRawElementVolume(element, channel, track.gain ?? 1);

    const pending: PendingPlayback = {
      channel,
      element,
      cancelled: false
    };
    let started = false;
    this.pendingPlaybacks.add(pending);

    try {
      for (const [sourceIndex, source] of sources.entries()) {
        if (pending.cancelled || this.destroyed) {
          return { status: "cancelled" };
        }

        try {
          element.src = source.src;
          element.currentTime = 0;
          element.load();
          await element.play();

          if (pending.cancelled || this.destroyed) {
            element.pause();
            return { status: "cancelled" };
          }

          started = true;
          return { status: "started", element, sources, sourceIndex };
        } catch (error) {
          element.pause();
          if (pending.cancelled || this.destroyed) {
            return { status: "cancelled" };
          }
          if (isAutoplayError(error)) {
            return { status: "blocked" };
          }
        }
      }
    } finally {
      this.pendingPlaybacks.delete(pending);
      if (!started) {
        this.releaseElement(element);
      }
    }

    return { status: "unavailable", reason: "media-error" };
  }

  private getSupportedSources(
    element: AudioMediaElement,
    sources: readonly AudioSource[]
  ): readonly AudioSource[] {
    return sources.filter((source) => {
      if (!source.type) {
        return true;
      }

      try {
        return element.canPlayType(source.type) !== "";
      } catch {
        return true;
      }
    });
  }

  private createActivePlayback(
    channel: AudioChannel,
    trackId: string,
    track: RuntimeTrackDefinition,
    started: Extract<StartElementResult, { readonly status: "started" }>,
    sourceState: {
      readonly sources: readonly AudioSource[];
      readonly sourceIndex: number;
      readonly lateFallbackAttempted: boolean;
    } = {
      sources: started.sources,
      sourceIndex: started.sourceIndex,
      lateFallbackAttempted: false
    }
  ): ActivePlayback {
    return {
      channel,
      trackId,
      track,
      element: started.element,
      ...sourceState,
      listeners: []
    };
  }

  private bindPlaybackEvents(active: ActivePlayback): void {
    this.bind(active, "ended", () => {
      this.handlePlaybackEnded(active);
    });
    this.bind(active, "error", () => {
      this.handlePlaybackError(active);
    });

    if (active.channel === "voice") {
      this.bind(active, "timeupdate", () => {
        const track = this.catalog.voice[active.trackId];
        if (track) {
          this.syncVoiceCaption(active, track);
        }
      });
    }
  }

  private bind(
    active: ActivePlayback,
    event: AudioMediaEvent,
    listener: EventListener
  ): void {
    active.element.addEventListener(event, listener);
    active.listeners.push({ event, listener });
  }

  private handlePlaybackEnded(active: ActivePlayback): void {
    if (active.channel === "music" && this.music === active) {
      this.cleanupPlayback(active, false);
      this.music = undefined;
      this.stateStore.setState({ activeMusicId: null });
      return;
    }

    if (active.channel === "voice" && this.voice === active) {
      this.cleanupPlayback(active, false);
      this.voice = undefined;
      this.stateStore.setState({ activeVoiceId: null, caption: null });
      this.applyAllVolumes();
      return;
    }

    if (active.channel === "sfx" && this.sfx.delete(active)) {
      this.cleanupPlayback(active, false);
      this.stateStore.setState({ activeSfxCount: this.sfx.size });
    }
  }

  private handlePlaybackError(active: ActivePlayback): void {
    const fallbackSourceIndex = this.getLateMp3FallbackIndex(active);
    if (
      fallbackSourceIndex !== null &&
      this.isCurrentPlayback(active)
    ) {
      void this.retryLateMediaFallback(active, fallbackSourceIndex);
      return;
    }

    this.finalizePlaybackError(active);
  }

  private async retryLateMediaFallback(
    active: ActivePlayback,
    sourceIndex: number
  ): Promise<void> {
    const source = active.sources[sourceIndex];
    if (!source || !this.isCurrentPlayback(active)) {
      return;
    }

    this.cleanupPlayback(active);
    if (active.channel === "voice") {
      const voiceTrack = this.catalog.voice[active.trackId];
      if (voiceTrack) {
        this.showFallbackCaption(active.trackId, voiceTrack);
      }
    }

    const started = await this.startElement(
      active.channel,
      active.track,
      active.element.loop,
      [source]
    );

    if (!this.isCurrentPlayback(active) || this.destroyed) {
      if (started.status === "started") {
        this.releaseElement(started.element);
      }
      return;
    }

    if (started.status === "blocked") {
      this.finalizeAutoplayBlocked(active);
      return;
    }

    if (started.status !== "started") {
      this.finalizePlaybackError(active);
      return;
    }

    const replacement = this.createActivePlayback(
      active.channel,
      active.trackId,
      active.track,
      started,
      {
        sources: active.sources,
        sourceIndex,
        lateFallbackAttempted: true
      }
    );
    this.replaceCurrentPlayback(active, replacement);
    this.bindPlaybackEvents(replacement);

    if (replacement.channel === "voice") {
      const voiceTrack = this.catalog.voice[replacement.trackId];
      if (voiceTrack) {
        this.syncVoiceCaption(replacement, voiceTrack);
      }
    }
    this.applyAllVolumes();
  }

  private finalizeAutoplayBlocked(active: ActivePlayback): void {
    if (active.channel === "music" && this.music === active) {
      this.cleanupPlayback(active);
      this.music = undefined;
      this.reportIssue("music", active.trackId, "autoplay-blocked", {
        activeMusicId: null,
        unlockState: "locked"
      });
      return;
    }

    if (active.channel === "voice" && this.voice === active) {
      const track = this.catalog.voice[active.trackId];
      this.cleanupPlayback(active);
      this.voice = undefined;
      this.reportIssue("voice", active.trackId, "autoplay-blocked", {
        activeVoiceId: null,
        caption: track
          ? toFallbackCaption(
              active.trackId,
              track.captions,
              track.fallbackCaption
            )
          : null,
        unlockState: "locked"
      });
      this.applyAllVolumes();
      return;
    }

    if (active.channel === "sfx" && this.sfx.delete(active)) {
      this.cleanupPlayback(active);
      this.reportIssue("sfx", active.trackId, "autoplay-blocked", {
        activeSfxCount: this.sfx.size,
        unlockState: "locked"
      });
    }
  }

  private finalizePlaybackError(active: ActivePlayback): void {
    if (active.channel === "music" && this.music === active) {
      this.cleanupPlayback(active);
      this.music = undefined;
      this.reportIssue("music", active.trackId, "media-error", {
        activeMusicId: null
      });
      return;
    }

    if (active.channel === "voice" && this.voice === active) {
      const track = this.catalog.voice[active.trackId];
      this.cleanupPlayback(active);
      this.voice = undefined;
      this.reportIssue("voice", active.trackId, "media-error", {
        activeVoiceId: null,
        caption: track
          ? toFallbackCaption(
              active.trackId,
              track.captions,
              track.fallbackCaption
            )
          : null
      });
      this.applyAllVolumes();
      return;
    }

    if (active.channel === "sfx" && this.sfx.delete(active)) {
      this.cleanupPlayback(active);
      this.reportIssue("sfx", active.trackId, "media-error", {
        activeSfxCount: this.sfx.size
      });
    }
  }

  private getLateMp3FallbackIndex(active: ActivePlayback): number | null {
    if (active.lateFallbackAttempted) {
      return null;
    }

    const currentSource = active.sources[active.sourceIndex];
    if (currentSource?.type !== "audio/ogg") {
      return null;
    }

    const nextMp3Index = active.sources.findIndex(
      (source, index) =>
        index > active.sourceIndex && source.type === "audio/mpeg"
    );

    return nextMp3Index === -1 ? null : nextMp3Index;
  }

  private isCurrentPlayback(active: ActivePlayback): boolean {
    switch (active.channel) {
      case "music":
        return this.music === active;
      case "voice":
        return this.voice === active;
      case "sfx":
        return this.sfx.has(active);
    }
  }

  private replaceCurrentPlayback(
    active: ActivePlayback,
    replacement: ActivePlayback
  ): void {
    switch (active.channel) {
      case "music":
        this.music = replacement;
        return;
      case "voice":
        this.voice = replacement;
        return;
      case "sfx":
        this.sfx.delete(active);
        this.sfx.add(replacement);
    }
  }

  private syncVoiceCaption(
    active: ActivePlayback,
    track: VoiceTrackDefinition
  ): void {
    if (this.voice !== active) {
      return;
    }

    const cue = getCaptionCueAt(
      track.captions,
      active.element.currentTime * 1_000
    );
    this.stateStore.setState({
      caption: cue ? toTimedCaption(active.trackId, cue) : null
    });
  }

  private showFallbackCaption(
    trackId: string,
    track: VoiceTrackDefinition
  ): void {
    this.stateStore.setState({
      caption: toFallbackCaption(
        trackId,
        track.captions,
        track.fallbackCaption
      )
    });
  }

  private releaseMusic(): void {
    if (!this.music) {
      return;
    }

    this.cleanupPlayback(this.music);
    this.music = undefined;
    this.stateStore.setState({ activeMusicId: null });
  }

  private releaseVoice(caption: CaptionDisplay | null): void {
    if (this.voice) {
      this.cleanupPlayback(this.voice);
      this.voice = undefined;
    }

    this.stateStore.setState({ activeVoiceId: null, caption });
    this.applyAllVolumes();
  }

  private cleanupPlayback(active: ActivePlayback, pause = true): void {
    for (const { event, listener } of active.listeners) {
      active.element.removeEventListener(event, listener);
    }
    active.listeners.length = 0;

    if (pause) {
      active.element.pause();
    }
    this.platform.releaseAudioElement?.(active.element);
  }

  private releaseElement(element: AudioMediaElement): void {
    element.pause();
    this.platform.releaseAudioElement?.(element);
  }

  private cancelPendingPlaybacks(channel?: AudioChannel): void {
    for (const pending of this.pendingPlaybacks) {
      if (channel !== undefined && pending.channel !== channel) {
        continue;
      }

      pending.cancelled = true;
      pending.element.pause();
    }
  }

  private applyAllVolumes(): void {
    if (this.music) {
      this.applyElementVolume(this.music);
    }
    if (this.voice) {
      this.applyElementVolume(this.voice);
    }
    for (const active of this.sfx) {
      this.applyElementVolume(active);
    }
  }

  private applyElementVolume(active: ActivePlayback): void {
    this.applyRawElementVolume(
      active.element,
      active.channel,
      active.track.gain ?? 1
    );
  }

  private applyRawElementVolume(
    element: AudioMediaElement,
    channel: AudioChannel,
    gain: number
  ): void {
    const preferences = this.preferencesStore.getState();
    const ducking = channel === "music" && this.voice ? this.duckingFactor : 1;
    element.muted = preferences.muted;
    element.volume = clampVolume(
      preferences.masterVolume *
        preferences.channelVolumes[channel] *
        clampVolume(gain) *
        ducking
    );
  }

  private handleStartFailure(
    channel: AudioChannel,
    trackId: string,
    result: Extract<
      StartElementResult,
      { readonly status: "blocked" | "unavailable" }
    >
  ): AudioPlaybackResult {
    if (result.status === "blocked") {
      this.stateStore.setState({ unlockState: "locked" });
      return this.blocked(channel, trackId, "autoplay-blocked");
    }

    return this.unavailable(channel, trackId, result.reason);
  }

  private blocked(
    channel: AudioChannel,
    trackId: string,
    reason: "autoplay-blocked" | "locked"
  ): AudioPlaybackResult {
    this.reportIssue(channel, trackId, reason);
    return { status: "blocked", channel, trackId, reason };
  }

  private unavailable(
    channel: AudioChannel,
    trackId: string | null,
    reason:
      | "destroyed"
      | "media-error"
      | "missing-track"
      | "nothing-to-replay"
      | "unsupported-browser"
      | "unsupported-format"
  ): AudioPlaybackResult {
    this.reportIssue(channel, trackId, reason);
    return { status: "unavailable", channel, trackId, reason };
  }

  private cancelled(
    channel: AudioChannel,
    trackId: string
  ): AudioPlaybackResult {
    return { status: "cancelled", channel, trackId, reason: "superseded" };
  }

  private reportIssue(
    channel: AudioChannel,
    trackId: string | null,
    reason: AudioIssueReason,
    statePatch: Partial<AudioRuntimeSnapshot> = {}
  ): void {
    this.stateStore.setState({
      ...statePatch,
      lastIssue: { channel, trackId, reason }
    });
  }
}

function isAutoplayError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return false;
  }

  return error.name === "NotAllowedError";
}
